import { Group, Stack } from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import {
  getFilteredRowModel,
  getPaginationRowModel,
  OnChangeFn,
  PaginationState,
} from "@tanstack/react-table"
import {
  ColumnManager,
  DEFAULT_PAGE_SIZE,
  ExportType,
  TextInput,
  ExportButton,
} from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import {
  TABLE_CONTEXT_MAP,
  ViewHeader,
} from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { useColumns } from "~/pages/Brand/Suppliers/List/SupplierProductionSites/columns.tsx"
import { useDownloadSuppliersProdSitesCSVMutation } from "~/pages/Brand/Suppliers/hooks"

interface Props {
  productionSitesData: LibraryResponses.SupplierProductionSitesResponse
  setPagination: OnChangeFn<PaginationState>
  pagination: PaginationState
  searchProductionSites: string
  setSearchProductionSites: (value: string) => void
  debouncedProductionSites: string
}

export const SupplierProductionSites = ({
  productionSitesData,
  setPagination,
  pagination,
  searchProductionSites,
  setSearchProductionSites,
  debouncedProductionSites,
}: Props) => {
  const { t } = useTranslation(["suppliers", "common"])
  const { count, productionSites, prodSiteFieldHeaders } = productionSitesData
  const columns = useColumns(prodSiteFieldHeaders)
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const { mutate: downloadMutation, isPending: isSuppliersCSVDownloading } =
    useDownloadSuppliersProdSitesCSVMutation({
      searchProductionSite: debouncedProductionSites,
    })
  const table = useCustomReactTable({
    data: productionSites,
    columns,
    initialColumnPinning: { left: ["name"] },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    },
    state: {
      globalFilter: searchProductionSites,
      pagination,
    },
    rowCount: count,
    onGlobalFilterChange: setSearchProductionSites,
    manualPagination: true,
    onPaginationChange: setPagination,

    getFilteredRowModel: getFilteredRowModel(),

    getPaginationRowModel: getPaginationRowModel(),
  })
  const columnManagerTranslations = useGetColumnManagerTranslations()
  return (
    <Stack>
      <Views
        tableContext={TABLE_CONTEXT_MAP.BUSINESS_PARTNERS_PRODUCTION_SITES}
      />
      <Group justify="space-between">
        <Group>
          <TextInput
            value={searchProductionSites}
            autoFocus
            onChange={(e) => setSearchProductionSites(e.target.value)}
            w={180}
            size="xs"
            leftSection={<IconSearch size={16} />}
            placeholder={t("list.searchPlaceholder")}
          />{" "}
          <ColumnManager
            table={table}
            translations={columnManagerTranslations}
            isOpen={isColumnManagerOpen}
            setIsOpen={setIsColumnManagerOpen}
          />
          <ViewHeader
            tableContext={TABLE_CONTEXT_MAP.BUSINESS_PARTNERS_PRODUCTION_SITES}
          />
        </Group>
        <ExportButton
          label={t("common:actionButtons.export")}
          exportOptions={[
            {
              type: ExportType.CSV,
              label: t("common:actionButtons.exportAsCsv"),
              exportFunction: downloadMutation,
              isLoading: isSuppliersCSVDownloading,
            },
          ]}
        />
      </Group>
      <CustomTable
        table={table}
        pagination={true}
        manualPaginationCount={count}
        stickyHeader
        setOpenColumnManager={setIsColumnManagerOpen}
      />
    </Stack>
  )
}
